import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  DollarSign,
  ArrowRight,
  TrendingUp,
  Target,
  Building2,
  Globe2,
  Users,
  ChevronRight,
  Loader2,
  Zap,
} from "lucide-react";
import Logo from "../components/Logo";
import { getBenchmark } from "../services/api";

const regions = [
  { id: "europe", label: "Europe", flag: "EU" },
  { id: "north_america", label: "North America", flag: "NA" },
  { id: "asia_pacific", label: "Asia Pacific", flag: "AP" },
  { id: "middle_east", label: "Middle East", flag: "ME" },
  { id: "latin_america", label: "Latin America", flag: "LA" },
];
const types = [
  { id: "public", label: "Public Hospital" },
  { id: "private", label: "Private Hospital" },
];
const sizes = [
  { id: "small", label: "Small", sub: "<200 beds" },
  { id: "medium", label: "Medium", sub: "200-600 beds" },
  { id: "large", label: "Large", sub: "600+ beds" },
];

function fmt$(v) {
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}

export default function BenchmarkPage() {
  const [step, setStep] = useState(0);
  const [region, setRegion] = useState("");
  const [hospitalType, setHospitalType] = useState("");
  const [size, setSize] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const res = await getBenchmark(region, hospitalType, size);
      setData(res.data);
      setStep(3);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const selectAndAdvance = (setter, value, nextStep) => {
    setter(value);
    if (nextStep === 3) {
      // Auto-generate
      setLoading(true);
      getBenchmark(
        nextStep === 3 && setter === setSize ? region : region,
        nextStep === 3 && setter === setSize ? hospitalType : hospitalType,
        value,
      )
        .then((res) => {
          setData(res.data);
          setStep(3);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setStep(nextStep);
    }
  };

  return (
    <div
      className="relative min-h-screen overflow-hidden bg-[#081126] flex flex-col"
      data-testid="benchmark-page"
      style={{
        backgroundImage: "url('/hppy_bg.png')",
        backgroundPosition: "center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-6">
        <div className="flex items-center gap-3">
          <Logo className="h-14 w-auto " mode="dark" />
        </div>
        <Link
          to="/login"
          className="px-4 py-2 text-sm font-semibold text-white bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
          data-testid="go-to-login"
        >
          Sign In
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 pb-12">
        <div className="w-full max-w-3xl">
          {/* Step 0: Region */}
          {step === 0 && (
            <div className="text-center animate-fade-in">
              <p className="text-emerald-400 text-sm font-semibold uppercase tracking-wider mb-3">
                Instant Benchmark
              </p>
              <h1 className="text-4xl md:text-5xl font-black text-white leading-tight mb-4">
                How much is your hospital
                <br />
                <span className="text-emerald-400">losing right now?</span>
              </h1>
              <p className="text-lg text-slate-400 mb-10">
                3 questions. 10 seconds. No login required.
              </p>

              <p className="text-sm text-slate-500 mb-4 uppercase tracking-wider font-semibold">
                Select your region
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-lg mx-auto">
                {regions.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => selectAndAdvance(setRegion, r.id, 1)}
                    className="bg-white/5 border border-white/10 rounded-xl p-4 text-left hover:bg-white/10 hover:border-emerald-400/50 transition-all group"
                    data-testid={`region-${r.id}`}
                  >
                    <span className="text-xs text-slate-500 font-bold">
                      {r.flag}
                    </span>
                    <p className="text-white font-semibold mt-1 group-hover:text-emerald-400 transition-colors">
                      {r.label}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 1: Type */}
          {step === 1 && (
            <div className="text-center animate-fade-in">
              <p className="text-emerald-400 text-sm font-semibold uppercase tracking-wider mb-3">
                Step 2 of 3
              </p>
              <h2 className="text-3xl font-bold text-white mb-8">
                Hospital type
              </h2>
              <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
                {types.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => selectAndAdvance(setHospitalType, t.id, 2)}
                    className="bg-white/5 border border-white/10 rounded-xl p-6 hover:bg-white/10 hover:border-emerald-400/50 transition-all group"
                    data-testid={`type-${t.id}`}
                  >
                    <Building2 className="w-8 h-8 text-slate-400 mx-auto mb-2 group-hover:text-emerald-400 transition-colors" />
                    <p className="text-white font-semibold group-hover:text-emerald-400 transition-colors">
                      {t.label}
                    </p>
                  </button>
                ))}
              </div>
              <button
                onClick={() => setStep(0)}
                className="mt-6 text-sm text-slate-500 hover:text-slate-300"
              >
                Back
              </button>
            </div>
          )}

          {/* Step 2: Size */}
          {step === 2 && (
            <div className="text-center animate-fade-in">
              <p className="text-emerald-400 text-sm font-semibold uppercase tracking-wider mb-3">
                Step 3 of 3
              </p>
              <h2 className="text-3xl font-bold text-white mb-8">
                Hospital size
              </h2>
              <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto">
                {sizes.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => selectAndAdvance(setSize, s.id, 3)}
                    className="bg-white/5 border border-white/10 rounded-xl p-6 hover:bg-white/10 hover:border-emerald-400/50 transition-all group"
                    data-testid={`size-${s.id}`}
                  >
                    <Users className="w-8 h-8 text-slate-400 mx-auto mb-2 group-hover:text-emerald-400 transition-colors" />
                    <p className="text-white font-semibold group-hover:text-emerald-400 transition-colors">
                      {s.label}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">{s.sub}</p>
                  </button>
                ))}
              </div>
              <button
                onClick={() => setStep(1)}
                className="mt-6 text-sm text-slate-500 hover:text-slate-300"
              >
                Back
              </button>
            </div>
          )}

          {/* Loading */}
          {loading && step !== 3 && (
            <div className="text-center py-20 animate-fade-in">
              <Loader2 className="w-12 h-12 text-emerald-400 animate-spin mx-auto mb-4" />
              <p className="text-lg text-white font-semibold">
                Analyzing peer hospital data...
              </p>
            </div>
          )}

          {/* Step 3: Results */}
          {step === 3 && data && (
            <div className="animate-fade-in">
              <div className="text-center mb-8">
                <p className="text-emerald-400 text-sm font-semibold uppercase tracking-wider mb-2">
                  Your Benchmark Result
                </p>
                <h2 className="text-3xl font-bold text-white">
                  What Hospitals Like Yours Are Losing
                </h2>
              </div>

              {/* Leakage Hero */}
              <div
                className="bg-white/5 border border-white/10 rounded-2xl p-8 mb-6"
                data-testid="benchmark-result"
              >
                <div className="text-center mb-6">
                  <p className="text-sm text-slate-400 mb-1">
                    Estimated Annual Leakage
                  </p>
                  <p className="text-5xl font-black text-red-400">
                    {fmt$(data.estimated_leakage.low)} –{" "}
                    {fmt$(data.estimated_leakage.high)}
                  </p>
                  <p className="text-sm text-slate-500 mt-2">
                    {data.peer_overspend_pct}% overspend vs optimized peers
                  </p>
                </div>

                {/* Category Breakdown */}
                <div className="space-y-3 mt-6">
                  {data.top_leakage_categories.map((cat, i) => {
                    const maxVal = data.top_leakage_categories[0].amount_high;
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1">
                          <div>
                            <span className="text-sm font-semibold text-white">
                              {cat.name}
                            </span>
                            <span className="text-xs text-slate-500 ml-2">
                              {cat.label}
                            </span>
                          </div>
                          <span className="text-sm font-bold text-red-400">
                            {fmt$(cat.amount_high)}
                          </span>
                        </div>
                        <div className="w-full bg-white/5 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-red-500 to-red-400 h-2 rounded-full transition-all duration-700"
                            style={{
                              width: `${(cat.amount_high / maxVal) * 100}%`,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Fix 3 Things */}
              <div
                className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-6 mb-6"
                data-testid="fix3-card"
              >
                <div className="flex items-center gap-3 mb-3">
                  <Zap className="w-6 h-6 text-emerald-400" />
                  <h3 className="text-lg font-bold text-white">
                    If You Fix Just These 3 Things
                  </h3>
                </div>
                <div className="flex items-center gap-6">
                  <div>
                    <p className="text-3xl font-black text-emerald-400">
                      {fmt$(data.fix3.savings)}
                    </p>
                    <p className="text-xs text-slate-400">Recoverable</p>
                  </div>
                  <div>
                    <p className="text-3xl font-black text-white">
                      {data.fix3.high_confidence}
                    </p>
                    <p className="text-xs text-slate-400">
                      High-confidence opportunities
                    </p>
                  </div>
                </div>
              </div>

              {/* ROI Preview */}
              <div
                className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-8"
                data-testid="roi-preview"
              >
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
                  ROI Preview
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-xs text-slate-500">Conservative</p>
                    <p className="text-xl font-bold text-slate-300">
                      {fmt$(data.estimated_leakage.low * 0.5)}
                    </p>
                  </div>
                  <div className="text-center border-x border-white/10">
                    <p className="text-xs text-emerald-400 font-semibold">
                      Expected
                    </p>
                    <p className="text-xl font-bold text-emerald-400">
                      {fmt$(data.estimated_leakage.high * 0.7)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500">Aggressive</p>
                    <p className="text-xl font-bold text-slate-300">
                      {fmt$(data.estimated_leakage.high)}
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-emerald-500 text-white font-bold rounded-xl hover:bg-emerald-400 transition-colors text-lg"
                  data-testid="benchmark-cta"
                >
                  Upload your data to see exact savings{" "}
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <p className="text-xs text-slate-500 mt-3">
                  Free analysis. No commitment. Results in 30 seconds.
                </p>
              </div>

              <button
                onClick={() => {
                  setStep(0);
                  setData(null);
                }}
                className="block mx-auto mt-6 text-sm text-slate-500 hover:text-slate-300"
              >
                Try different hospital profile
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
